<script setup lang="ts">
import { computed, ref, type InputHTMLAttributes } from 'vue';
import { type BaseTextInputProps } from './internal';
import { maskValue } from './composables/maskValue';
import NxTextInput from './NxTextInput.vue';

const props = defineProps<
  BaseTextInputProps & {
    /**
     * Regular expression matching the substring(s) to hide. Matched characters
     * are replaced 1:1 with `maskChar` while the field is blurred. Pass the `g`
     * flag to mask every occurrence (e.g. `/\d(?=\d{4})/g`); without it only the
     * first match is hidden.
     */
    mask: RegExp;
    /** Character shown in place of each hidden character. Defaults to `'•'`. */
    maskChar?: string;
  } & /* @vue-ignore */ Omit<InputHTMLAttributes, 'value'>
>();

const [valueModel, valueModelModifiers] = defineModel<string, 'lazy'>({ required: true });

const isActive = ref(false);

// Editable buffer used while the field is focused. Mirrors NxCurrencyInput's
// shadow-value pattern so the `.lazy` modifier can defer committing to
// `valueModel` until blur.
const shadowValue = ref('');

const inputModel = computed<string>({
  get() {
    // While focused the user edits the real value; while blurred we show the mask.
    if (isActive.value) return shadowValue.value;
    return maskValue(valueModel.value, props.mask, props.maskChar);
  },
  set(newValue) {
    shadowValue.value = newValue;
    if (!valueModelModifiers.lazy) valueModel.value = newValue;
  },
});

const onFocus = () => {
  // Reveal: seed the editable buffer with the real value, then unmask.
  shadowValue.value = valueModel.value;
  isActive.value = true;
};

const onBlur = () => {
  // Commit a pending lazy edit, then re-mask.
  if (valueModelModifiers.lazy) valueModel.value = shadowValue.value;
  isActive.value = false;
};
</script>

<template>
  <NxTextInput
    v-bind="{ ...props, mask: undefined, maskChar: undefined }"
    v-model="inputModel"
    @focus="onFocus"
    @focusout="onBlur"
  />
</template>
